<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useCalendarStore, useEvaluationStore, useEventsModalsStore, useTherapyStore } from '@/store';
import {
  AttendeeDropdownOptionInterface,
  Attendee,
  EventDeleteRequestInterface,
  DetailedEventInterface,
} from '@/types';
import { usePersonTypes } from '@/composables';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import DisciplineIcon from '@/components/DisciplineIcon/DisciplineIcon.vue';
import EventModal from '@/views/Calendar/components/EventModal/EventModal.vue';
import DocumentEventModal from '@/views/Calendar/components/DocumentEventModal.vue';
import PastDocumentations from '@/views/Calendar/components/PastDocumentations.vue';
import { storeToRefs } from 'pinia';
import { EventClickArg } from '@fullcalendar/core';
import InfoTooltip from '../InfoTooltip/InfoTooltip.vue';

const evaluationStore = useEvaluationStore();
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const therapyStore = useTherapyStore();

const { eventToEditSelected, displayEventModal, eventToDocumentOrEdit, displayDocumentEventModal } =
  storeToRefs(eventsModalsStore);

const toast = useToast();
const selectedAttendees = ref<AttendeeDropdownOptionInterface[]>([]);
const displayPastDocumentations = ref(false);
const pastDocumentationsStudent = ref();
const confirmDeleteOpen = ref(false);
const deleteEventConditions = ref<'this' | 'future'>('this');
const isSupportAdminOrSchoolAdmin = ref(usePersonTypes.isSupportAdminOrSchoolAdmin());
const multiAttendee = ref(0);

const eventTypeTitle = computed(() => {
  const needPrefix = ['Session', 'Response to Intervention'].includes(
    eventToDocumentOrEdit.value?.therapy_event_type as string,
  );
  const prefix = needPrefix && multiAttendee.value > 1 ? 'Group' : 'Individual';

  return needPrefix
    ? `${prefix} ${eventToDocumentOrEdit.value?.therapy_event_type}`
    : eventToDocumentOrEdit.value?.therapy_event_type;
});

const modalTitle = computed(() => (eventToDocumentOrEdit.value ? `Edit ${eventTypeTitle.value}` : 'New Event'));

const togglePastDocuments = async (attendee: Attendee) => {
  if (calendarStore.pastDocumentationsPaginateState[attendee.id] === undefined) {
    await calendarStore.getPastDocumentations(eventToDocumentOrEdit.value?.document_date as string, attendee.id);
  }

  if (!displayPastDocumentations.value) {
    pastDocumentationsStudent.value = attendee;
    displayPastDocumentations.value = true;
    return;
  }

  if (pastDocumentationsStudent.value.id === attendee.id) {
    displayPastDocumentations.value = false;
    return;
  }

  pastDocumentationsStudent.value = attendee;
};

const loadStudentPastSessions = async () => {
  await calendarStore.getPastDocumentations(
    eventToDocumentOrEdit.value?.document_date as string,
    pastDocumentationsStudent.value.id,
  );
};

const closePastDocuments = () => {
  displayPastDocumentations.value = false;
};

const toggleDocumentEventModal = () => {
  displayDocumentEventModal.value = !displayDocumentEventModal.value;
  if (!displayEventModal.value) displayPastDocumentations.value = false;
};

const editEvent = () => {
  toggleDocumentEventModal();
  eventsModalsStore.onEditEvent(eventsModalsStore.clickedEventClickArgs as EventClickArg['event']);
};

const onSaveDocumentEvent = async (responseStatus: number, responseText: string) => {
  await calendarStore.getEvents({ start_at: calendarStore.startDate, end_at: calendarStore.endDate });

  if (responseStatus === 200) {
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Your event has been documented.',
      life: 3000,
    });
    toggleDocumentEventModal();
    return;
  }

  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: responseText,
    life: 3000,
  });
};

const documentFutureEvent = () => {
  eventsModalsStore.toggleEventModal();
  eventsModalsStore.onDocumentEvent(eventsModalsStore.clickedEventClickArgs as EventClickArg['event']);
};

const saveNewEventFailed = (errorResponse: { data: { message?: string; errors: Record<string, string[]> } }) => {
  const errorArr = ['Your event was unable to be created:', errorResponse.data.message];
  const detail: string = errorArr.join('\n');

  displayEventModal.value = false;
  toast.add({
    severity: 'error',
    summary: 'Event Creation Failed',
    detail,
    life: 3000,
  });
};

const deleteEventSuccess = async () => {
  displayEventModal.value = false;
  toast.add({
    severity: 'success',
    summary: 'Event Deleted',
    detail: 'Your event has been deleted successfully!',
    life: 3000,
  });

  await calendarStore.getEvents({ start_at: calendarStore.startDate, end_at: calendarStore.endDate });
};

const deleteEvent = async () => {
  const payload: EventDeleteRequestInterface = {
    id: eventToDocumentOrEdit.value?.id as number,
    date: eventToDocumentOrEdit.value?.start_at as string,
    mode: deleteEventConditions.value,
  };
  await therapyStore.deleteEvent(payload).catch(deleteEventFailed);
  await deleteEventSuccess();
};

const deleteEventFailed = (errorResponse: { message?: string; errors: Record<string, string[]> }) => {
  const errorArr = ['Your event failed to delete.', errorResponse.message];
  const detail: string = errorArr.join('\n');

  displayEventModal.value = false;
  toast.add({
    severity: 'error',
    summary: 'Event Delete Failed',
    detail,
    life: 3000,
  });
};

const saveNewEvent = async () => {
  displayEventModal.value = false;
  selectedAttendees.value = [];
  toast.add({
    severity: 'success',
    summary: 'Event Created',
    detail: 'Your event has been successfully created!',
    life: 3000,
  });

  await calendarStore.getEvents({ start_at: calendarStore.startDate, end_at: calendarStore.endDate });
  await evaluationStore.getEvaluations();
};

const changeMultiAttendee = (value: Attendee[]) => (multiAttendee.value = value.length);
</script>

<template>
  <div>
    <Dialog v-model:visible="displayEventModal" :modal="true" :draggable="false" class="new-event-modal">
      <template #header>
        <h2>{{ modalTitle }}</h2>
        <InfoTooltip v-if="eventToDocumentOrEdit && eventToDocumentOrEdit.host_timezone_name">
          <span>Event ID: {{ eventToDocumentOrEdit.id }}</span>
          <span>
            Created:
            {{ eventToDocumentOrEdit.created_at }}
            {{ eventToDocumentOrEdit.host_timezone_abbr }}
          </span>
        </InfoTooltip>
        <Icon
          v-if="eventToEditSelected"
          name="delete"
          class="material-icons p-dialog-header-icon trash-can-icon ml-auto cursor-pointer"
          @click="confirmDeleteOpen = true"
        />
        <div v-if="calendarStore.isCaseloadManagement(eventTypeTitle) || eventsModalsStore.isSingleEvent">
          <ConfirmAction
            v-if="confirmDeleteOpen"
            header="Delete Event"
            accept-label="Delete Event"
            @accept="deleteEvent"
            @cancel="confirmDeleteOpen = false"
          >
            <div class="flex flex-column w-20rem">
              <div class="flex">
                <Icon name="warning" class="text-color-yellow-dark" />
                <span class="inline-block mt-1 ml-2 text-base" v-text="'This action cannot be undone'" />
              </div>
            </div>
          </ConfirmAction>
        </div>
        <div v-else>
          <ConfirmAction
            v-if="confirmDeleteOpen"
            header="Delete Recurring Event"
            accept-label="Delete"
            @accept="deleteEvent"
            @cancel="confirmDeleteOpen = false"
          >
            <div class="flex flex-column w-20rem font-bold">
              <div class="mb-2">
                <RadioButton v-model="deleteEventConditions" name="deleteThis" value="this" />
                <label for="deleteThis" class="ml-2">This event</label>
              </div>
              <div>
                <RadioButton v-model="deleteEventConditions" name="deleteAll" value="future" />
                <label for="deleteAll" class="ml-2">This and following events</label>
              </div>
            </div>
          </ConfirmAction>
        </div>
      </template>
      <Suspense>
        <EventModal
          v-if="eventToDocumentOrEdit"
          :event-to-edit="(eventToDocumentOrEdit as DetailedEventInterface)"
          :event-to-edit-selected="eventToEditSelected"
          @save-new-event="saveNewEvent"
          @save-event-failed="saveNewEventFailed"
          @close-modal="eventsModalsStore.toggleEventModal()"
          @document-event="documentFutureEvent"
        />
        <EventModal
          v-else
          :event-to-edit="eventToDocumentOrEdit"
          :event-to-edit-selected="eventToEditSelected"
          @save-new-event="saveNewEvent"
          @save-event-failed="saveNewEventFailed"
          @close-modal="eventsModalsStore.toggleEventModal()"
        />
      </Suspense>
    </Dialog>
    <div :class="{ 'document-event-modal-with-drawer': displayPastDocumentations }">
      <Dialog
        v-model:visible="displayDocumentEventModal"
        append-to="self"
        :modal="true"
        :draggable="false"
        class="document-event-modal"
      >
        <template #header>
          <div class="flex align-items-center">
            <h2>{{ !isSupportAdminOrSchoolAdmin ? 'Document ' + eventTypeTitle : eventTypeTitle }}</h2>
            <DisciplineIcon
              v-if="eventToDocumentOrEdit && eventToDocumentOrEdit.discipline"
              class="mx-2"
              :code="eventToDocumentOrEdit.discipline.code"
            />
            <InfoTooltip v-if="eventToDocumentOrEdit && eventToDocumentOrEdit.host_timezone_name">
              <span>Event ID: {{ eventToDocumentOrEdit.id }}</span>
              <span>
                Created:{{ eventToDocumentOrEdit.created_at }}
                {{ eventToDocumentOrEdit.host_timezone_abbr }}
              </span>
            </InfoTooltip>
          </div>
        </template>
        <DocumentEventModal
          :document-modal-opened="displayDocumentEventModal && displayPastDocumentations"
          :event-to-document="eventToDocumentOrEdit"
          @open-past-documents="togglePastDocuments"
          @close-modal="toggleDocumentEventModal"
          @save-document-event="onSaveDocumentEvent"
          @edit-event="editEvent"
          @change-multi-attendee="changeMultiAttendee"
        />
      </Dialog>
    </div>

    <PastDocumentations
      :student="pastDocumentationsStudent"
      :open="displayDocumentEventModal && displayPastDocumentations"
      @load-student-past-sessions="loadStudentPastSessions"
      @close-past-documentations="closePastDocuments"
    />
  </div>
</template>
